import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Typography,
  Backdrop,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  useDatastoreFiles,
  useFetchAllWorkspaceFiles,
  useGetWorkspaceFiles,
  useIsLoading,
  useIsNetworkRequestPending,
  useSelectedDataset,
  useSetLoading,
  useResetValidationState,
  useSetValidationState,
  useUpdateValidationState,
  useValidationState,
} from '../../state/hooks/DatastoreHooks';
import { useDataStoreFileDownload } from './DataStoreHooks';
import {
  ActionsToolbar,
  DataStoreItemsList,
  DataStoreItemDetails,
  DatastoreMetadataCard,
  DataStoreTablesList,
  StatusChip,
} from './components';

const DataStore = () => {
  const { t } = useTranslation();
  const [isMetadataOpen, setIsMetadataOpen] = useState(false);
  const isUILoading = useIsLoading();
  const setLoading = useSetLoading();
  const isNetworkRequestPending = useIsNetworkRequestPending();
  const showBackdrop = isUILoading || isNetworkRequestPending;

  const validationState = useValidationState();
  const resetValidationState = useResetValidationState();
  const setValidationState = useSetValidationState();
  const updateValidationState = useUpdateValidationState();

  const dataset = useSelectedDataset();
  const datastoreFiles = useDatastoreFiles();

  const isMounted = useRef(true);

  const downloadFile = useDataStoreFileDownload();
  const fetchAllFiles = useFetchAllWorkspaceFiles();
  const workspaceFiles = useGetWorkspaceFiles();

  useEffect(() => {
    return () => {
      setLoading(true);
      resetValidationState();
    };
  }, [dataset?.id, setLoading, resetValidationState]);

  useEffect(() => {
    const applyEffect = async () => {
      isMounted.current = true;

      const validationFile = datastoreFiles.find((file) => file.name === '_validation.json');

      if (!validationFile) {
        updateValidationState({ status: 'draft' });
        setLoading(false);
        return;
      }

      try {
        if (validationState?.status == null || validationState?.status === 'draft') {
          const { data } = await downloadFile(validationFile.storagePath);
          if (isMounted.current) setValidationState(data);
        }
      } catch (error) {
        console.error('Error downloading validation file:', error);
      } finally {
        setLoading(false);
      }
    };

    applyEffect();
  }, [datastoreFiles, downloadFile, setLoading, setValidationState, updateValidationState, validationState?.status]);

  useEffect(() => {
    isMounted.current = true;
    if (workspaceFiles.length === 0) {
      setLoading(true);
      fetchAllFiles();
    }

    return () => {
      isMounted.current = false;
      resetValidationState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const datastoreMetadata = useMemo(() => {
    return <DatastoreMetadataCard />;
  }, []);

  const noSelectionPlaceholder = useMemo(() => {
    if (dataset != null) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="h6">
          {t('commoncomponents.datastore.details.placeholder.text', 'Please select a datastore to work with')}
        </Typography>
      </Box>
    );
  }, [t, dataset]);

  return (
    <Grid
      container
      direction="column"
      sx={{ gap: 3, height: '100%', p: 2, overflow: 'auto', display: 'flex' }}
      wrap="nowrap"
    >
      <Backdrop
        data-cy="datastore-view-backdrop"
        open={showBackdrop}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress />
      </Backdrop>
      <Grid item id="top">
        <Grid container sx={{ justifyContent: 'space-between' }} wrap="wrap">
          <Grid container item sx={{ flex: '4 1 0', alignItems: 'center', p: 1 }}>
            <DataStoreItemsList />
            <StatusChip />
            {dataset && (
              <Tooltip
                title={t('commoncomponents.datastore.details.metadata.buttonLabel', 'Voir les informations du Magasin')}
              >
                <IconButton
                  onClick={() => setIsMetadataOpen(true)}
                  size="small"
                  sx={{ ml: 1 }}
                  data-cy="datastore-metadata-button"
                  aria-label={t(
                    'commoncomponents.datastore.details.metadata.buttonLabel',
                    'Voir les informations du Magasin'
                  )}
                >
                  <InfoOutlined />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
          <Grid item sx={{ flex: '4 1 0' }}>
            <ActionsToolbar />
          </Grid>
        </Grid>
      </Grid>
      <Dialog
        open={isMetadataOpen}
        onClose={() => setIsMetadataOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="datastore-metadata-dialog-title"
      >
        <DialogTitle id="datastore-metadata-dialog-title">
          {t('commoncomponents.datastore.details.metadata.title', 'Informations du Magasin')}
        </DialogTitle>
        <DialogContent>{datastoreMetadata}</DialogContent>
        <DialogActions>
          <Button onClick={() => setIsMetadataOpen(false)}>{t('commoncomponents.dialog.close', 'Fermer')}</Button>
        </DialogActions>
      </Dialog>
      <Grid item id="validation-details">
        <DataStoreItemDetails />
        {noSelectionPlaceholder}
      </Grid>
      <Grid item id="files-list" sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <DataStoreTablesList />
      </Grid>
    </Grid>
  );
};

export default DataStore;
